import React, { Component } from 'react';
import { Link } from 'react-router-dom';

import AuthService from './AuthService';
import constants from '../config';
import { PAYPAL_PAYOUTS_ENABLED } from '../config/constants';
import { resolveCardImage } from '../utils/resolveImage';

const Auth = new AuthService();

var ITEMS_PER_PAGE = 10;
var FLAT_SHIPPING_FEE = constants.FLAT_SHIPPING_FEE || 1.00;
var FREE_SHIPPING_THRESHOLD = constants.FREE_SHIPPING_THRESHOLD || 10;

function getStatus(order, isSale) {
  if (order.payoutStatus === 'disputed') return 'disputed';
  if (order.payoutStatus === 'refunded') return 'refunded';
  if (order.payoutStatus === 'payout_failed' && isSale) return 'payout_failed';
  if (order.payoutStatus === 'awaiting_stripe_connect' && isSale) return 'awaiting_stripe_connect';
  if (order.sellerPaid || order.payoutStatus === 'released' || order.payoutStatus === 'payout_failed') return 'completed';
  if (order.shippedAt) return 'shipped';
  if (order.trackingNumber && order.trackingNumber.length > 0) return 'shipped';
  return 'awaiting';
}

function getStatusLabel(status) {
  if (status === 'disputed') return 'Disputed';
  if (status === 'refunded') return 'Refunded';
  if (status === 'payout_failed') return 'Payout Pending';
  if (status === 'awaiting_stripe_connect') return 'Connect Stripe';
  if (status === 'completed') return 'Funds Released';
  if (status === 'shipped') return 'Shipped';
  if (status === 'partially_shipped') return 'Partially Shipped';
  return 'Awaiting Shipment';
}

function getStatusClass(status) {
  if (status === 'disputed') return 'tw-bg-red-500/10 tw-text-red-500';
  if (status === 'refunded') return 'tw-bg-gray-500/10 tw-text-gray-500';
  if (status === 'payout_failed') return 'tw-bg-orange-500/10 tw-text-orange-500';
  if (status === 'awaiting_stripe_connect') return 'tw-bg-amber-500/10 tw-text-amber-600';
  if (status === 'completed') return 'tw-bg-green-600/10 tw-text-green-600';
  if (status === 'shipped') return 'tw-bg-blue-500/10 tw-text-blue-500';
  if (status === 'partially_shipped') return 'tw-bg-blue-500/10 tw-text-blue-500';
  return 'tw-bg-yellow-500/10 tw-text-yellow-600';
}

function getDaysUntilRelease(payoutScheduledAt) {
  if (!payoutScheduledAt) return null;
  var now = new Date();
  var release = new Date(payoutScheduledAt);
  var diff = Math.ceil((release - now) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : 0;
}

function getTrackingUrl(carrier, trackingNumber) {
  if (carrier === 'USPS') return 'https://tools.usps.com/go/TrackConfirmAction?tLabels=' + trackingNumber;
  if (carrier === 'UPS') return 'https://www.ups.com/track?tracknum=' + trackingNumber;
  if (carrier === 'FedEx') return 'https://www.fedex.com/fedextrack/?trknbr=' + trackingNumber;
  return 'https://www.google.com/search?q=' + trackingNumber + '+tracking';
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  var d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatShortDate(dateStr) {
  if (!dateStr) return '';
  var d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function renderProgressBar(order, isSale) {
  var hasTracking = order.trackingNumber && order.trackingNumber.length > 0;
  var isShipped = !!order.shippedAt || hasTracking;
  var isDelivered = order.payoutStatus === 'released' || order.payoutStatus === 'payout_failed' || order.payoutStatus === 'awaiting_stripe_connect' || order.sellerPaid;
  var isReviewed = !!order.reviewed;
  var isDisputed = order.payoutStatus === 'disputed';
  var isRefunded = order.payoutStatus === 'refunded';

  var steps = [
    { label: 'Ordered', done: true, date: order.createdAt },
    { label: 'Shipped', done: isShipped, date: order.shippedAt },
    { label: 'Delivered', done: isDelivered, date: order.deliveredAt },
    { label: isSale ? 'Paid Out' : 'Reviewed', done: isSale ? isDelivered : isReviewed, date: null },
  ];

  if (isDisputed) {
    steps[2] = { label: 'Disputed', done: false, special: '#ef4444', date: order.disputedAt };
    steps[3].done = false;
  }
  if (isRefunded) {
    steps[2] = { label: 'Refunded', done: false, special: '#6b7280', date: null };
    steps[3].done = false;
  }

  var activeIdx = steps.length;
  for (var i = 0; i < steps.length; i++) {
    if (!steps[i].done) { activeIdx = i; break; }
  }

  var checkSvg = React.createElement('svg', {
    width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none',
    stroke: 'white', strokeWidth: 3, strokeLinecap: 'round', strokeLinejoin: 'round',
  }, React.createElement('polyline', { points: '20 6 9 17 4 12' }));

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%', padding: '8px 0' }}>
      {steps.map(function(step, idx) {
        var isDone = step.done;
        var isActive = idx === activeIdx && !step.special;
        var isSpecial = !!step.special;

        var circleColor = step.special || (isDone ? '#22c55e' : isActive ? 'transparent' : '#d1d5db');
        var circleBorder = isActive ? '3px solid #d4a843' : isDone ? 'none' : isSpecial ? 'none' : '2px solid #d1d5db';
        var lineColor = isDone ? '#22c55e' : step.special || '#e5e7eb';

        return (
          <React.Fragment key={idx}>
            {idx > 0 ? (
              <div style={{
                flex: 1, height: '3px', marginTop: '13px',
                backgroundColor: lineColor, minWidth: '16px',
                borderRadius: '2px',
              }}></div>
            ) : null}
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              gap: '6px', minWidth: '0', flexShrink: 0,
            }}>
              <div style={{
                width: '28px', height: '28px', borderRadius: '50%',
                backgroundColor: circleColor, border: circleBorder,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
                boxShadow: isDone ? '0 1px 3px rgba(34,197,94,0.3)' : isActive ? '0 0 0 4px rgba(212,168,67,0.15)' : 'none',
                transition: 'all 0.3s ease',
              }}>
                {isDone ? checkSvg : null}
                {isSpecial ? (
                  <span style={{ color: 'white', fontSize: '14px', fontWeight: '700', lineHeight: 1 }}>!</span>
                ) : null}
              </div>
              <span style={{
                fontSize: '11px',
                fontWeight: isActive || isSpecial || isDone ? '600' : '400',
                color: step.special || (isDone ? '#22c55e' : isActive ? '#d4a843' : '#9ca3af'),
                lineHeight: 1.2, whiteSpace: 'nowrap', textAlign: 'center',
              }}>{step.label}</span>
              {isDone && step.date ? (
                <span style={{
                  fontSize: '10px', color: '#9ca3af', lineHeight: 1, marginTop: '-2px',
                }}>{formatShortDate(step.date)}</span>
              ) : null}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function groupOrders(orders) {
  var groups = [];
  var seen = {};
  orders.forEach(function(order) {
    var paymentKey = order.stripeChargeId || order.paypalOrderID || order._id;
    var sellerId = (order.seller && order.seller._id) ? order.seller._id : 'unknown';
    var sellerName = (order.seller && order.seller.userName) ? order.seller.userName : 'Unknown';
    var sellerKey = paymentKey + '_' + sellerId;

    if (seen[paymentKey]) {
      var group = seen[paymentKey];
      group.items.push(order);
      // Find or create seller sub-group
      var foundSeller = false;
      for (var i = 0; i < group.sellerGroups.length; i++) {
        if (group.sellerGroups[i].sellerKey === sellerKey) {
          group.sellerGroups[i].items.push(order);
          foundSeller = true;
          break;
        }
      }
      if (!foundSeller) {
        group.sellerGroups.push({
          sellerKey: sellerKey,
          sellerId: sellerId,
          sellerName: sellerName,
          items: [order],
          primary: order,
        });
      }
    } else {
      var group = {
        key: paymentKey,
        items: [order],
        primary: order,
        sellerGroups: [{
          sellerKey: sellerKey,
          sellerId: sellerId,
          sellerName: sellerName,
          items: [order],
          primary: order,
        }],
        isMultiSeller: false,
      };
      seen[paymentKey] = group;
      groups.push(group);
    }
  });
  // Set isMultiSeller flag
  groups.forEach(function(group) {
    group.isMultiSeller = group.sellerGroups.length > 1;
  });
  return groups;
}

function getGroupStatus(group, isSale) {
  var dominated = { disputed: 5, refunded: 4, payout_failed: 3.5, awaiting: 3, partially_shipped: 2.5, shipped: 2, completed: 1 };
  var worst = 'completed';
  group.items.forEach(function(order) {
    var s = getStatus(order, isSale);
    if ((dominated[s] || 0) > (dominated[worst] || 0)) worst = s;
  });
  // For multi-seller orders: if worst is "awaiting" but some items are shipped, show "partially_shipped"
  if (group.isMultiSeller && worst === 'awaiting') {
    var anyShipped = group.items.some(function(order) { return getStatus(order, isSale) === 'shipped' || getStatus(order, isSale) === 'completed'; });
    if (anyShipped) worst = 'partially_shipped';
  }
  return worst;
}

function getGroupTotal(group, isSale) {
  var itemTotal = 0;
  var trackingFees = 0;
  var taxTotal = 0;
  var txFeeTotal = 0;
  var payoutTotal = 0;
  var sellerSubtotals = {};
  group.items.forEach(function(order) {
    var price = order.itemPrice || (order.post ? order.post.price : 0) || 0;
    itemTotal += price;
    trackingFees += order.trackingUpgradeFee || 0;
    taxTotal += order.taxAmount || 0;
    payoutTotal += Number(order.sellerPayout) || 0;
    // Use stored transactionFee (undefined for old orders = no fee)
    if (typeof order.transactionFee === 'number') {
      txFeeTotal += order.transactionFee;
    }
    // Track per-seller subtotals for shipping calculation
    var sid = (order.seller && order.seller._id) ? String(order.seller._id) : 'unknown';
    sellerSubtotals[sid] = (sellerSubtotals[sid] || 0) + price;
  });
  // Shipping is per-seller: flat fee if that seller's subtotal < threshold
  var shipping = 0;
  Object.keys(sellerSubtotals).forEach(function(sid) {
    if (sellerSubtotals[sid] < FREE_SHIPPING_THRESHOLD) {
      shipping += FLAT_SHIPPING_FEE;
    }
  });
  var total = itemTotal + shipping + trackingFees;
  if (!isSale) {
    total += txFeeTotal + taxTotal;
  }
  return { itemTotal: itemTotal, shipping: shipping, trackingFees: trackingFees, transactionFee: txFeeTotal, taxTotal: taxTotal, payoutTotal: payoutTotal, total: total };
}

function validateTracking(carrier, trackingNumber) {
  var num = trackingNumber.trim();
  if (!num) return 'Please enter a tracking number';
  if (num.length < 8) return 'Tracking number is too short';
  if (carrier === 'USPS' && !/^[0-9]{20,22}$|^[A-Z]{2}[0-9]{9}US$/i.test(num))
    return 'Invalid USPS tracking number';
  if (carrier === 'UPS' && !/^1Z[A-Z0-9]{16}$/i.test(num))
    return 'Invalid UPS tracking number';
  if (carrier === 'FedEx' && !/^[0-9]{12,22}$/.test(num))
    return 'Invalid FedEx tracking number';
  if (!/^[A-Za-z0-9]+$/.test(num)) return 'Tracking number can only contain letters and numbers';
  return null;
}

class OrderHistory extends Component {
  constructor(props) {
    super(props);
    this.state = {
      subTab: 'purchases',
      purchases: [],
      sales: [],
      purchaseGroups: [],
      saleGroups: [],
      earnings: [],
      reviews: [],
      loading: true,
      statusFilter: 'all',
      page: 0,
      expandedOrderId: null,
      revenuePeriod: 'month',
      trackingInputs: {},
      carrierInputs: {},
      trackingErrors: {},
      shippingOrderId: null,
      confirmingOrderId: null,
      disputeOrderId: null,
      disputeReason: '',
      disputeDescription: '',
      submittingDispute: false,
      reviewOrderId: null,
      reviewRating: 0,
      reviewText: '',
      submittingReview: false,
      hoverRating: 0,
      messagingOrderId: null,
      messageModalOtherId: null,
      messageModalPostIds: [],
      messageModalGroupKey: null,
      messageModalText: '',
      messageModalIsSale: false,
      unclaimedPayouts: 0,
      activeSellerTab: {},
    };
    this.fetchOrders = this.fetchOrders.bind(this);
    this.findGroup = this.findGroup.bind(this);
    this.findSellerGroup = this.findSellerGroup.bind(this);
    this.submitTracking = this.submitTracking.bind(this);
    this.markAsShipped = this.markAsShipped.bind(this);
    this.confirmReceipt = this.confirmReceipt.bind(this);
    this.openDispute = this.openDispute.bind(this);
    this.submitDispute = this.submitDispute.bind(this);
    this.submitReview = this.submitReview.bind(this);
    this.messageUser = this.messageUser.bind(this);
    this.sendAndNavigate = this.sendAndNavigate.bind(this);
  }

  componentDidMount() {
    this.fetchOrders();
    this._handleThemeChanged = function() { this.forceUpdate(); }.bind(this);
    window.addEventListener('themeChanged', this._handleThemeChanged);
  }

  componentDidUpdate() {
    var anyModalOpen = !!(this.state.disputeOrderId || this.state.reviewOrderId || this.state.messageModalOtherId);
    document.body.style.overflow = anyModalOpen ? 'hidden' : '';
  }

  componentWillUnmount() {
    document.body.style.overflow = '';
    if (this._handleThemeChanged) {
      window.removeEventListener('themeChanged', this._handleThemeChanged);
    }
  }

  fetchOrders() {
    var self = this;
    Auth.fetch(constants.DOMAIN + '/api/v1/transactions/list', {
      mode: 'cors',
      method: 'GET',
    }).then(function(res) {
      // Mark completed orders (has review for that transaction)
      var reviewedSellers = {};
      (res.reviews || []).forEach(function(r) {
        if (r.buyer) reviewedSellers[r.buyer._id] = true;
      });

      var purchases = (res.purchased || []).reverse();
      var sales = (res.sales || []).reverse();
      self.setState({
        purchases: purchases,
        sales: sales,
        purchaseGroups: groupOrders(purchases),
        saleGroups: groupOrders(sales),
        earnings: (res.marketed || []).reverse(),
        reviews: res.reviews || [],
        loading: false,
        unclaimedPayouts: res.unclaimedPayouts || 0,
      });
    }).catch(function(err) {
      console.log(err);
      self.setState({ loading: false });
    });
  }

  findGroup(groupKey, listName) {
    var groups = listName === 'sales' ? this.state.saleGroups : this.state.purchaseGroups;
    for (var i = 0; i < groups.length; i++) {
      if (groups[i].key === groupKey) return groups[i];
    }
    return null;
  }

  findSellerGroup(sellerKey, listName) {
    var groups = listName === 'sales' ? this.state.saleGroups : this.state.purchaseGroups;
    for (var i = 0; i < groups.length; i++) {
      var sg = groups[i].sellerGroups;
      for (var j = 0; j < sg.length; j++) {
        if (sg[j].sellerKey === sellerKey) return sg[j];
      }
    }
    return null;
  }

  submitTracking(sellerKey) {
    var trackingNumber = this.state.trackingInputs[sellerKey] || '';
    var carrier = this.state.carrierInputs[sellerKey] || '';

    var error = validateTracking(carrier, trackingNumber);
    if (error) {
      var errors = Object.assign({}, this.state.trackingErrors);
      errors[sellerKey] = error;
      this.setState({ trackingErrors: errors });
      window.dispatchEvent(new CustomEvent('toast', { detail: { text: error, type: 'info' } }));
      return;
    }

    var sellerGroup = this.findSellerGroup(sellerKey, 'sales');
    if (!sellerGroup) return;
    var orderIds = sellerGroup.items.map(function(item) { return item._id; });

    var self = this;
    this.setState({ shippingOrderId: sellerKey });

    function shipOne(idx) {
      if (idx >= orderIds.length) {
        // All done — optimistically update
        var idSet = {};
        orderIds.forEach(function(id) { idSet[id] = true; });
        var updatedSales = self.state.sales.map(function(sale) {
          if (idSet[sale._id]) {
            return Object.assign({}, sale, { trackingNumber: trackingNumber.trim(), carrier: carrier });
          }
          return sale;
        });
        var newTrackingInputs = Object.assign({}, self.state.trackingInputs);
        delete newTrackingInputs[sellerKey];
        var newCarrierInputs = Object.assign({}, self.state.carrierInputs);
        delete newCarrierInputs[sellerKey];
        var newErrors = Object.assign({}, self.state.trackingErrors);
        delete newErrors[sellerKey];

        self.setState({
          sales: updatedSales,
          saleGroups: groupOrders(updatedSales),
          trackingInputs: newTrackingInputs,
          carrierInputs: newCarrierInputs,
          trackingErrors: newErrors,
          shippingOrderId: null,
        });
        window.dispatchEvent(new CustomEvent('toast', { detail: { text: 'Tracking number saved!', type: 'success' } }));
        self.fetchOrders();
        return;
      }

      var data = new URLSearchParams();
      data.append('tracking', trackingNumber.trim());
      if (carrier) data.append('carrier', carrier);

      Auth.fetch(constants.DOMAIN + '/api/v1/transactions/' + orderIds[idx] + '/ship', {
        mode: 'cors',
        method: 'POST',
        body: data,
      }).then(function() {
        shipOne(idx + 1);
      }).catch(function(err) {
        console.log(err);
        self.setState({ shippingOrderId: null });
        window.dispatchEvent(new CustomEvent('toast', { detail: { text: 'Failed to save tracking number', type: 'error' } }));
      });
    }

    shipOne(0);
  }

  markAsShipped(sellerKey) {
    var sellerGroup = this.findSellerGroup(sellerKey, 'sales');
    if (!sellerGroup) return;
    var orderIds = sellerGroup.items.map(function(item) { return item._id; });

    var self = this;
    this.setState({ shippingOrderId: sellerKey });

    function shipOne(idx) {
      if (idx >= orderIds.length) {
        var idSet = {};
        orderIds.forEach(function(id) { idSet[id] = true; });
        var updatedSales = self.state.sales.map(function(sale) {
          if (idSet[sale._id]) {
            return Object.assign({}, sale, { shippedAt: new Date().toISOString() });
          }
          return sale;
        });
        self.setState({ sales: updatedSales, saleGroups: groupOrders(updatedSales), shippingOrderId: null });
        window.dispatchEvent(new CustomEvent('toast', { detail: { text: 'Order marked as shipped!', type: 'success' } }));
        self.fetchOrders();
        return;
      }

      Auth.fetch(constants.DOMAIN + '/api/v1/transactions/' + orderIds[idx] + '/ship', {
        mode: 'cors',
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: '',
      }).then(function() {
        shipOne(idx + 1);
      }).catch(function(err) {
        console.log(err);
        self.setState({ shippingOrderId: null });
        window.dispatchEvent(new CustomEvent('toast', { detail: { text: 'Failed to mark as shipped', type: 'error' } }));
      });
    }

    shipOne(0);
  }

  messageUser(otherId, postIds, groupKey, isSale) {
    if (!otherId) return;
    this.setState({
      messageModalOtherId: otherId,
      messageModalPostIds: postIds,
      messageModalGroupKey: groupKey,
      messageModalText: '',
      messageModalIsSale: !!isSale,
    });
  }

  sendAndNavigate() {
    var self = this;
    var otherId = this.state.messageModalOtherId;
    var messageText = (this.state.messageModalText || '').trim();
    if (!otherId) return;

    this.setState({ messagingOrderId: this.state.messageModalGroupKey, messageModalOtherId: null });

    var msgBody = { userId: otherId };

    // Use the first transaction ID from the order group as attached order context
    var attachedOrderId = null;
    if (self.state.messageModalGroupKey) {
      var activeTab = self.state.activeTab;
      var txns = activeTab === 'purchases' ? self.state.transactions : self.state.salesTransactions;
      if (txns) {
        for (var ti = 0; ti < txns.length; ti++) {
          if (txns[ti]._id && String(txns[ti]._id) === String(self.state.messageModalGroupKey)) {
            attachedOrderId = txns[ti]._id;
            break;
          }
        }
      }
      if (!attachedOrderId) attachedOrderId = self.state.messageModalGroupKey;
    }

    Auth.fetch(constants.DOMAIN + '/api/v1/messages/', {
      mode: 'cors',
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(msgBody),
    }).then(function(res) {
      var conv = res && res.conversation;
      if (conv && conv._id && messageText) {
        var sendBody = { text: messageText };
        if (attachedOrderId) sendBody.attachedOrderId = attachedOrderId;
        // Send the message, then navigate
        return Auth.fetch(constants.DOMAIN + '/api/v1/messages/' + conv._id, {
          mode: 'cors',
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(sendBody),
        }).then(function() {
          self.setState({ messagingOrderId: null });
          self.props.history.push('/messages', { conversationId: conv._id });
        });
      } else if (conv && conv._id) {
        self.setState({ messagingOrderId: null });
        self.props.history.push('/messages', { conversationId: conv._id });
      } else {
        self.setState({ messagingOrderId: null });
        self.props.history.push('/messages');
      }
    }).catch(function(err) {
      console.log('Message error:', err);
      self.setState({ messagingOrderId: null });
      window.dispatchEvent(new CustomEvent('toast', { detail: { text: 'Failed to open conversation', type: 'error' } }));
    });
  }

  confirmReceipt(sellerKey) {
    var sellerGroup = this.findSellerGroup(sellerKey, 'purchases');
    if (!sellerGroup) return;
    var orderIds = sellerGroup.items.map(function(item) { return item._id; });

    var self = this;
    this.setState({ confirmingOrderId: sellerKey });

    function confirmOne(idx) {
      if (idx >= orderIds.length) {
        var idSet = {};
        orderIds.forEach(function(id) { idSet[id] = true; });
        var updatedPurchases = self.state.purchases.map(function(p) {
          if (idSet[p._id]) {
            return Object.assign({}, p, { payoutStatus: 'released', deliveredAt: new Date().toISOString() });
          }
          return p;
        });
        self.setState({
          confirmingOrderId: null,
          purchases: updatedPurchases,
          purchaseGroups: groupOrders(updatedPurchases),
          reviewOrderId: orderIds[0],
          reviewRating: 0, reviewText: '', hoverRating: 0,
        });
        window.dispatchEvent(new CustomEvent('toast', { detail: { text: 'Receipt confirmed! Seller has been paid.', type: 'success' } }));
        self.fetchOrders();
        return;
      }

      Auth.fetch(constants.DOMAIN + '/api/v1/transactions/' + orderIds[idx] + '/confirm-receipt', {
        mode: 'cors',
        method: 'POST',
      }).then(function() {
        confirmOne(idx + 1);
      }).catch(function(err) {
        console.log(err);
        self.setState({ confirmingOrderId: null });
        window.dispatchEvent(new CustomEvent('toast', { detail: { text: 'Failed to confirm receipt', type: 'error' } }));
      });
    }

    confirmOne(0);
  }

  openDispute(sellerKey) {
    this.setState({ disputeOrderId: sellerKey, disputeReason: '', disputeDescription: '' });
  }

  submitDispute() {
    var self = this;
    var sellerKey = this.state.disputeOrderId;
    var sellerGroup = this.findSellerGroup(sellerKey, 'purchases');
    if (!sellerGroup) return;
    var orderIds = sellerGroup.items.map(function(item) { return item._id; });

    var reason = this.state.disputeReason;
    if (!reason) {
      window.dispatchEvent(new CustomEvent('toast', { detail: { text: 'Please select a reason', type: 'info' } }));
      return;
    }
    var fullReason = reason;
    if (this.state.disputeDescription) {
      fullReason += ': ' + this.state.disputeDescription;
    }
    this.setState({ submittingDispute: true });

    function disputeOne(idx) {
      if (idx >= orderIds.length) {
        var idSet = {};
        orderIds.forEach(function(id) { idSet[id] = true; });
        var updatedPurchases = self.state.purchases.map(function(p) {
          if (idSet[p._id]) {
            return Object.assign({}, p, { payoutStatus: 'disputed' });
          }
          return p;
        });
        self.setState({
          submittingDispute: false, disputeOrderId: null,
          disputeReason: '', disputeDescription: '',
          purchases: updatedPurchases,
          purchaseGroups: groupOrders(updatedPurchases),
        });
        window.dispatchEvent(new CustomEvent('toast', { detail: { text: 'Dispute opened. Our team will review this order.', type: 'info' } }));
        self.fetchOrders();
        return;
      }

      fetch(constants.DOMAIN + '/api/v1/transactions/' + orderIds[idx] + '/dispute', {
        mode: 'cors',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': Auth.getAuthHeader(),
        },
        body: JSON.stringify({ reason: fullReason }),
      }).then(function(r) {
        if (!r.ok) throw new Error(r.statusText);
        return r.json();
      }).then(function() {
        disputeOne(idx + 1);
      }).catch(function(err) {
        console.log(err);
        self.setState({ submittingDispute: false });
        window.dispatchEvent(new CustomEvent('toast', { detail: { text: 'Failed to open dispute', type: 'error' } }));
      });
    }

    disputeOne(0);
  }

  submitReview() {
    var self = this;
    var orderId = this.state.reviewOrderId;
    var rating = this.state.reviewRating;
    var reviewText = this.state.reviewText.trim();

    if (!rating) {
      window.dispatchEvent(new CustomEvent('toast', { detail: { text: 'Please select a star rating', type: 'info' } }));
      return;
    }

    this.setState({ submittingReview: true });
    var body = { rating: rating };
    if (reviewText) body.review = reviewText;
    fetch(constants.DOMAIN + '/api/v1/reviews/' + orderId, {
      mode: 'cors',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': Auth.getAuthHeader(),
      },
      body: JSON.stringify(body),
    }).then(function(r) {
      if (!r.ok) throw new Error(r.statusText);
      return r.json();
    }).then(function() {
      // Optimistically mark order as reviewed
      var updatedPurchases = self.state.purchases.map(function(p) {
        if (p._id === orderId) {
          return Object.assign({}, p, { reviewed: true });
        }
        return p;
      });
      self.setState({ submittingReview: false, reviewOrderId: null, reviewRating: 0, reviewText: '', hoverRating: 0, purchases: updatedPurchases, purchaseGroups: groupOrders(updatedPurchases) });
      window.dispatchEvent(new CustomEvent('toast', { detail: { text: 'Review submitted! Thank you.', type: 'success' } }));
      self.fetchOrders();
    }).catch(function(err) {
      console.log(err);
      self.setState({ submittingReview: false });
      window.dispatchEvent(new CustomEvent('toast', { detail: { text: 'Failed to submit review', type: 'error' } }));
    });
  }

  renderEarningsRow(order) {
    var isDark = document.documentElement.classList.contains('tw-dark');
    var post = order.post || {};
    var image = resolveCardImage(post.cardImage) || resolveCardImage(post.images && post.images[0]) || '';
    var title = post.title || 'Untitled';
    var commission = Number(order.marketerPayout) || 0;
    var date = formatDate(order.createdAt);

    var statusLabel, statusClass;
    if (order.marketerPaid) {
      statusLabel = 'Paid';
      statusClass = 'tw-bg-green-600/10 tw-text-green-600';
    } else if (order.marketerPayoutDeferred) {
      statusLabel = 'Unclaimed';
      statusClass = 'tw-bg-orange-500/10 tw-text-orange-500';
    } else if (order.payoutStatus === 'disputed') {
      statusLabel = 'Disputed';
      statusClass = 'tw-bg-red-500/10 tw-text-red-500';
    } else if (order.payoutStatus === 'refunded') {
      statusLabel = 'Refunded';
      statusClass = 'tw-bg-gray-500/10 tw-text-gray-500';
    } else if (order.trackingNumber && order.trackingNumber.length > 0) {
      statusLabel = 'Shipped';
      statusClass = 'tw-bg-blue-500/10 tw-text-blue-500';
    } else {
      statusLabel = 'Awaiting Shipment';
      statusClass = 'tw-bg-yellow-500/10 tw-text-yellow-600';
    }

    var earningsHoverKey = '_earningsHover_' + order._id;
    var self = this;
    return (
      <div key={order._id} className="tw-rounded-xl tw-p-4 tw-mb-3"
        style={{
          backgroundColor: isDark ? '#1a1a1a' : '#fff',
          border: isDark ? '1px solid #2a2a2a' : '1px solid #e0e0e0',
          transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
          boxShadow: this[earningsHoverKey] ? '0 2px 8px rgba(0,0,0,0.06)' : 'none',
        }}
        onMouseEnter={function() { self[earningsHoverKey] = true; self.forceUpdate(); }}
        onMouseLeave={function() { self[earningsHoverKey] = false; self.forceUpdate(); }}
      >
        <div className="tw-flex tw-gap-4">
          {image ? (
            <Link to={'/product/' + post._id} className="tw-flex-shrink-0">
              <img
                src={image}
                alt={title}
                className="tw-rounded tw-object-cover"
                style={{ width: '48px', height: '67px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
              />
            </Link>
          ) : (
            <div className="tw-rounded tw-flex-shrink-0" style={{ width: '48px', height: '67px', backgroundColor: isDark ? '#0f0f0f' : '#f5f5f5' }}></div>
          )}
          <div className="tw-flex-1 tw-min-w-0">
            <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
              <div className="tw-min-w-0">
                <Link to={'/product/' + (post._id || '')} className="tw-text-sm tw-font-medium hover:tw-text-brand-gold tw-transition-colors tw-block tw-truncate tw-no-underline" style={{ color: isDark ? '#fff' : '#111' }}>
                  {title}
                </Link>
                <p className="tw-text-xs tw-mt-1" style={{ color: '#888' }}>Promoted sale</p>
              </div>
              <div className="tw-text-right tw-flex-shrink-0">
                <p className="tw-text-sm tw-font-semibold tw-mb-0" style={{ color: '#16a34a' }}>
                  +${commission.toFixed(2)}
                </p>
                <span className={'tw-text-[10px] tw-font-medium tw-px-2 tw-py-0.5 tw-rounded-full tw-inline-block tw-mt-1 ' + statusClass}>
                  {statusLabel}
                </span>
              </div>
            </div>
            <p className="tw-text-xs tw-mt-2 tw-mb-0" style={{ color: '#888' }}>{date}</p>
          </div>
        </div>
      </div>
    );
  }

  renderOrderRow(group, isSale) {
    var self = this;
    var isDark = document.documentElement.classList.contains('tw-dark');
    var isMulti = group.items.length > 1;
    var order = group.primary;
    var post = order.post || {};

    // For groups, use group-level status and totals
    var status = isMulti ? getGroupStatus(group, isSale) : getStatus(order, isSale);
    var statusLabel = getStatusLabel(status);
    var statusClass = getStatusClass(status);
    var totals = getGroupTotal(group, isSale);
    var price = isSale && totals.payoutTotal > 0 ? totals.payoutTotal : totals.total;
    var date = formatDate(order.createdAt);
    var otherUser = isSale ? order.buyer : order.seller;
    var otherName = otherUser ? (otherUser.userName || 'Unknown') : (isSale ? (order.guestName || order.guestEmail || 'Guest Buyer') : 'Unknown');
    // For multi-seller purchases, show "N sellers" instead of one name
    if (group.isMultiSeller && !isSale) {
      otherName = group.sellerGroups.length + ' sellers';
    }
    var isExpanded = this.state.expandedOrderId === group.key;

    // Title: first item + count
    var title = post.title || 'Untitled';
    if (isMulti) {
      title = title + ' (+' + (group.items.length - 1) + ' more)';
    }

    // Images for stacked thumbnail
    var images = [];
    group.items.forEach(function(item) {
      var p = item.post || {};
      if (images.length < 3) {
        var img = resolveCardImage(p.cardImage) || resolveCardImage(p.images && p.images[0]);
        if (img) images.push(img);
      }
    });

    // Hover state key for this row
    var hoverKey = '_rowHover_' + group.key;

    return (
      <div key={group.key} className="tw-mb-3" style={{
        borderRadius: '12px', overflow: 'hidden',
        border: isExpanded ? '1px solid #d4a843' : (isDark ? '1px solid #2a2a2a' : '1px solid #e8e8e8'),
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        boxShadow: isExpanded ? '0 4px 16px rgba(212,168,67,0.1)' : (this[hoverKey] ? '0 2px 8px rgba(0,0,0,0.06)' : 'none'),
      }}>
        {/* Compact Summary Row */}
        <div
          onClick={function() {
            self.setState({ expandedOrderId: isExpanded ? null : group.key });
          }}
          onMouseEnter={function() { self[hoverKey] = true; self.forceUpdate(); }}
          onMouseLeave={function() { self[hoverKey] = false; self.forceUpdate(); }}
          className="tw-cursor-pointer"
          style={{
            padding: '16px 20px',
            borderLeft: isExpanded ? '3px solid #d4a843' : '3px solid transparent',
            transition: 'background-color 0.2s ease, border-color 0.2s ease',
            backgroundColor: isExpanded
              ? (isDark ? '#1c1a14' : '#fffbf0')
              : (self[hoverKey]
                ? (isDark ? '#1e1e1e' : '#f2f2f2')
                : (isDark ? '#161616' : '#fafafa')),
          }}
        >
          <div className="tw-flex tw-items-center tw-gap-3">
            {/* Thumbnail — stacked for multi, single for solo */}
            {isMulti ? (
              <div style={{ width: '52px', height: '72px', position: 'relative', flexShrink: 0 }}>
                {images.map(function(src, idx) {
                  return (
                    <img
                      key={idx}
                      src={src}
                      alt=""
                      style={{
                        width: '42px', height: '58px', objectFit: 'cover',
                        position: 'absolute',
                        top: idx * 4 + 'px', left: idx * 4 + 'px',
                        borderRadius: '6px',
                        zIndex: 3 - idx,
                        border: '1px solid rgba(0,0,0,0.08)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      }}
                    />
                  );
                })}
              </div>
            ) : (
              images.length > 0 ? (
                <img
                  src={images[0]}
                  alt={title}
                  style={{ width: '56px', height: '78px', objectFit: 'cover', flexShrink: 0, borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                />
              ) : (
                <div style={{ width: '56px', height: '78px', flexShrink: 0, borderRadius: '8px', backgroundColor: isDark ? '#0f0f0f' : '#f5f5f5' }}></div>
              )
            )}

            {/* Title + User + Date */}
            <div className="tw-flex-1 tw-min-w-0">
              <p className="tw-text-sm tw-font-medium tw-truncate tw-mb-0" style={{ color: isDark ? '#fff' : '#111' }}>{title}</p>
              <p className="tw-text-xs tw-mb-0 tw-mt-0.5" style={{ color: '#888' }}>
                {isSale ? 'Buyer: ' : 'Seller: '}{otherName} &middot; {date} &middot; <span style={{ fontFamily: 'monospace', letterSpacing: '0.5px' }}>#{order._id ? order._id.slice(-8).toUpperCase() : ''}</span>
              </p>
            </div>

            {/* Price + Badge + Chevron */}
            <div className="tw-flex tw-items-center tw-gap-3 tw-flex-shrink-0">
              <div className="tw-text-right tw-hidden sm:tw-block">
                <p className="tw-text-sm tw-font-semibold tw-mb-0" style={{ color: isDark ? '#fff' : '#111' }}>
                  ${price.toFixed(2)}
                </p>
              </div>
              <span className={'tw-text-xs tw-font-semibold tw-px-2.5 tw-py-1 tw-rounded-full tw-whitespace-nowrap ' + statusClass}>
                {statusLabel}
              </span>
              <i
                className="fa fa-chevron-down tw-text-xs"
                style={{
                  color: isDark ? '#555' : '#bbb',
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }}
              ></i>
            </div>
          </div>
        </div>

        {/* Expanded Detail Panel — smooth accordion */}
        <div className={'order-accordion' + (isExpanded ? ' order-accordion-expanded' : '')}>
          {this.renderOrderDetail(group, isSale, isDark)}
        </div>
      </div>
    );
  }

  renderSellerTabContent(sellerGroup, group, isSale) {
    var self = this;
    var sg = sellerGroup;
    var sgOrder = sg.primary;
    var sgStatus = getGroupStatus(sg, isSale);
    var sgIsMulti = sg.items.length > 1;

    var otherUser = isSale ? sgOrder.buyer : sgOrder.seller;
    var otherName = otherUser ? (otherUser.userName || 'Unknown') : (isSale ? (sgOrder.guestName || sgOrder.guestEmail || 'Guest Buyer') : 'Unknown');
    var otherId = otherUser ? otherUser._id : '';

    var anyUnshipped = sg.items.some(function(item) { return !item.shippedAt && !item.trackingNumber; });
    var anyTrackingUpgrade = sg.items.some(function(item) { return item.trackingUpgrade; });
    var anyShippedAndHeld = sg.items.some(function(item) { return item.shippedAt && item.payoutStatus === 'held'; });
    var anyUnreviewed = sg.items.some(function(item) { return !item.reviewed && (item.payoutStatus === 'released' || item.sellerPaid); });

    return (
      <div key={sg.sellerKey}>
        {/* Seller link + shipping address */}
        <p className="tw-text-xs tw-text-[#888] tw-mb-1">
          {isSale ? 'Buyer: ' : 'Seller: '}
          {otherUser ? (
            <Link to={'/shop/' + otherName} className="tw-text-[#111] dark:tw-text-white tw-font-medium tw-no-underline hover:tw-text-brand-gold tw-transition-colors">
              {otherName}
            </Link>
          ) : (
            <span className="tw-text-[#111] dark:tw-text-white tw-font-medium">{otherName}{sgOrder.guestEmail ? ' (' + sgOrder.guestEmail + ')' : ''}</span>
          )}
        </p>
        {/* Shipping Address (both seller and buyer view) */}
        {sgOrder.shippingAddress && sgOrder.shippingAddress.street1 ? (
          <div className="tw-mt-2 tw-mb-2">
            <p className="tw-text-xs tw-text-[#888] tw-mb-0.5">{isSale ? 'Ship To:' : 'Shipping Address:'}</p>
            <p className="tw-text-sm tw-text-[#111] dark:tw-text-white tw-mb-0 tw-leading-snug">
              {sgOrder.shippingTo || (isSale ? otherName : '')}{(sgOrder.shippingTo || (isSale ? otherName : '')) ? <br /> : null}
              {sgOrder.shippingAddress.street1}{sgOrder.shippingAddress.street2 ? ', ' + sgOrder.shippingAddress.street2 : ''}<br />
              {sgOrder.shippingAddress.city}{sgOrder.shippingAddress.state ? ', ' + sgOrder.shippingAddress.state : ''} {sgOrder.shippingAddress.zip}
              {sgOrder.shippingAddress.country && sgOrder.shippingAddress.country !== 'US' ? ', ' + sgOrder.shippingAddress.country : ''}
            </p>
          </div>
        ) : null}

        {/* Per-seller subtotal for multi-seller orders */}
        {group.isMultiSeller ? (function() {
          var sgTotals = getGroupTotal(sg, isSale);
          return (
            <p className="tw-text-xs tw-text-[#888] tw-mt-2 tw-mb-3">
              {sg.items.length} item{sg.items.length > 1 ? 's' : ''} &middot; Subtotal: <span className="tw-font-semibold tw-text-[#111] dark:tw-text-white">${sgTotals.itemTotal.toFixed(2)}</span>
            </p>
          );
        })() : null}

        {/* Progress bar */}
        <div className="tw-mb-5" style={{ maxWidth: '420px' }}>
          {renderProgressBar(sgOrder, isSale)}
        </div>

        {/* Divider before tracking/actions */}
        <div className="tw-border-t tw-border-[#e5e5e5] dark:tw-border-[#2a2a2a] tw-my-4"></div>

        {/* Dispute Info Banner */}
        {sgStatus === 'disputed' ? (
          <div className="tw-mb-4 tw-rounded-lg tw-p-4" style={{ backgroundColor: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)' }}>
            <div className="tw-flex tw-gap-3">
              <i className="fa fa-exclamation-triangle tw-text-red-500 tw-mt-0.5"></i>
              <div className="tw-flex-1">
                <p className="tw-text-sm tw-font-semibold tw-text-red-600 dark:tw-text-red-400 tw-mb-1">Dispute Under Review</p>
                <p className="tw-text-xs tw-text-[#666] dark:tw-text-[#aaa] tw-mb-2">
                  Our moderation team is reviewing this dispute and will reach out to both parties. Please allow <span className="tw-font-semibold tw-text-[#333] dark:tw-text-white">3-5 business days</span> for resolution.
                </p>
                {sgOrder.disputeReason ? (
                  <div className="tw-mt-2 tw-pt-2" style={{ borderTop: '1px solid rgba(239,68,68,0.12)' }}>
                    <p className="tw-text-[10px] tw-font-medium tw-text-[#999] tw-uppercase tw-tracking-wide tw-mb-1">Reason</p>
                    <p className="tw-text-xs tw-text-[#555] dark:tw-text-[#bbb] tw-mb-0">{sgOrder.disputeReason}</p>
                  </div>
                ) : null}
                {sgOrder.disputedAt ? (
                  <p className="tw-text-[10px] tw-text-[#999] tw-mt-2 tw-mb-0">Opened {formatDate(sgOrder.disputedAt)}</p>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        {/* Refund Info Banner */}
        {sgStatus === 'refunded' ? (
          <div className="tw-mb-4 tw-rounded-lg tw-p-4" style={{ backgroundColor: 'rgba(107,114,128,0.06)', border: '1px solid rgba(107,114,128,0.15)' }}>
            <div className="tw-flex tw-gap-3">
              <i className="fa fa-undo tw-text-[#6b7280] tw-mt-0.5"></i>
              <div>
                <p className="tw-text-sm tw-font-semibold tw-text-[#6b7280] tw-mb-1">Order Refunded</p>
                <p className="tw-text-xs tw-text-[#666] dark:tw-text-[#aaa] tw-mb-0">
                  This order has been refunded. The funds have been returned to the original payment method. Please allow 5-10 business days for the refund to appear on your statement.
                </p>
              </div>
            </div>
          </div>
        ) : null}

        {/* Tracking & Payout Status */}
        <div className="tw-mb-4">
          {sgOrder.trackingNumber ? (
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
              <i className="fa fa-truck tw-text-[#888] tw-text-sm"></i>
              <a
                href={getTrackingUrl(sgOrder.carrier, sgOrder.trackingNumber)}
                target="_blank"
                rel="noopener noreferrer"
                className="tw-text-sm tw-text-blue-500 hover:tw-text-blue-400 tw-transition-colors tw-no-underline"
              >
                {sgOrder.carrier ? sgOrder.carrier + ' — ' : ''}{sgOrder.trackingNumber}
              </a>
            </div>
          ) : null}

          {/* Seller payout status */}
          {isSale && sgOrder.shippedAt ? (
            sgOrder.sellerPaid || sgOrder.payoutStatus === 'released' ? (
              <div className="tw-flex tw-items-center tw-gap-2">
                <i className="fa fa-check-circle tw-text-green-600"></i>
                <span className="tw-text-sm tw-text-green-600 tw-font-medium">Funds Released</span>
              </div>
            ) : sgOrder.payoutStatus === 'payout_failed' ? (
              <div className="tw-flex tw-items-center tw-gap-2">
                <i className="fa fa-exclamation-circle tw-text-orange-500"></i>
                <span className="tw-text-sm tw-text-orange-500 tw-font-medium">Payout Pending</span>
                <Link to="/settings" className="tw-text-xs tw-text-blue-500 hover:tw-text-blue-400 tw-no-underline">Set up payout method</Link>
              </div>
            ) : sgOrder.payoutStatus === 'disputed' ? (
              <div className="tw-flex tw-items-center tw-gap-2">
                <i className="fa fa-exclamation-circle tw-text-red-500"></i>
                <span className="tw-text-sm tw-text-red-500 tw-font-medium">Disputed</span>
              </div>
            ) : (function() {
              var days = getDaysUntilRelease(sgOrder.payoutScheduledAt);
              return (
                <div className="tw-flex tw-items-start tw-gap-2">
                  <i className="fa fa-clock-o tw-mt-0.5" style={{ color: '#ca8a04' }}></i>
                  <div>
                    <Link to="/app#for-sellers" className="tw-text-sm tw-font-medium tw-no-underline hover:tw-underline" style={{ color: '#ca8a04' }}>
                      Funds Held{days !== null ? ' \u2014 Releases in ' + days + ' day' + (days !== 1 ? 's' : '') : ''}
                    </Link>
                    <br className="sm:tw-hidden" />
                    <span className="tw-text-xs" style={{ color: '#999' }}> or when buyer confirms receipt</span>
                  </div>
                </div>
              );
            })()
          ) : !isSale && sgOrder.shippedAt && (sgOrder.payoutStatus === 'released' || sgOrder.payoutStatus === 'payout_failed') ? (
            <div className="tw-flex tw-items-center tw-gap-2">
              <i className="fa fa-check-circle tw-text-green-600"></i>
              <span className="tw-text-sm tw-text-green-600 tw-font-medium">Funds Released</span>
            </div>
          ) : !isSale && anyShippedAndHeld ? (
            <p className="tw-text-xs tw-mb-0 tw-mt-1" style={{ color: '#888' }}>
              <i className="fa fa-info-circle tw-mr-1"></i>
              Received your order? Confirm receipt to release the seller&apos;s funds.
            </p>
          ) : null}
        </div>

        {/* Ship order actions for unshipped orders (seller) */}
        {isSale && anyUnshipped ? (
          <p className="tw-text-xs tw-text-[#888] tw-mt-2 tw-mb-0"><Link to="/shipping" className="tw-text-brand-gold hover:tw-text-brand-gold-dark tw-no-underline">View shipping guide</Link> for packaging tips.</p>
        ) : null}
        {isSale && anyUnshipped ? (
          anyTrackingUpgrade ? (
            <div className="tw-mt-2">
              <p className="tw-text-xs tw-font-medium tw-text-[#888] tw-mb-2">Add tracking to ship this order{sgIsMulti ? ' (' + sg.items.length + ' items)' : ''}</p>
              <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-2">
                <select
                  className="tw-bg-white dark:tw-bg-[#0f0f0f] tw-rounded-lg tw-px-4 tw-py-2.5 tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-text-[#111] dark:tw-text-white tw-text-sm tw-outline-none"
                  value={this.state.carrierInputs && this.state.carrierInputs[sg.sellerKey] || ''}
                  onChange={function(e) {
                    var inputs = Object.assign({}, self.state.carrierInputs || {});
                    inputs[sg.sellerKey] = e.target.value;
                    self.setState({ carrierInputs: inputs });
                  }}
                >
                  <option value="">Carrier</option>
                  <option value="USPS">USPS</option>
                  <option value="UPS">UPS</option>
                  <option value="FedEx">FedEx</option>
                </select>
                <input
                  type="text"
                  className="tw-flex-1 tw-bg-white dark:tw-bg-[#0f0f0f] tw-rounded-lg tw-px-4 tw-py-2.5 tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] focus:tw-border-brand-gold tw-outline-none tw-text-[#111] dark:tw-text-white tw-text-sm"
                  placeholder="Enter tracking number"
                  value={this.state.trackingInputs[sg.sellerKey] || ''}
                  onChange={function(e) {
                    var inputs = Object.assign({}, self.state.trackingInputs);
                    inputs[sg.sellerKey] = e.target.value;
                    self.setState({ trackingInputs: inputs });
                  }}
                  onClick={function(e) { e.stopPropagation(); }}
                />
                <button
                  onClick={function(e) { e.stopPropagation(); self.submitTracking(sg.sellerKey); }}
                  disabled={self.state.shippingOrderId === sg.sellerKey}
                  className="tw-px-5 tw-py-2.5 tw-rounded-lg tw-bg-brand-gold tw-text-white tw-text-sm tw-font-semibold tw-border-none tw-cursor-pointer hover:tw-bg-brand-gold/90 tw-transition-colors tw-flex-shrink-0 disabled:tw-opacity-60 disabled:tw-cursor-not-allowed"
                >
                  {self.state.shippingOrderId === sg.sellerKey ? 'Shipping...' : 'Ship'}
                </button>
              </div>
              {self.state.trackingErrors[sg.sellerKey] ? (
                <p className="tw-text-red-500 tw-text-xs tw-mt-1">{self.state.trackingErrors[sg.sellerKey]}</p>
              ) : null}
            </div>
          ) : (
            <div className="tw-mt-2">
              <p className="tw-text-xs tw-text-[#888] tw-mb-2">No tracking required for this order</p>
              <button
                onClick={function(e) { e.stopPropagation(); self.markAsShipped(sg.sellerKey); }}
                disabled={self.state.shippingOrderId === sg.sellerKey}
                className="tw-w-full sm:tw-w-auto tw-px-5 tw-py-2.5 tw-rounded-lg tw-bg-brand-gold tw-text-white tw-text-sm tw-font-semibold tw-border-none tw-cursor-pointer hover:tw-bg-brand-gold/90 tw-transition-colors disabled:tw-opacity-60 disabled:tw-cursor-not-allowed"
              >
                {self.state.shippingOrderId === sg.sellerKey ? 'Marking Shipped...' : 'Mark as Shipped'}
              </button>
            </div>
          )
        ) : null}

        {/* Action buttons */}
        <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mt-3">
          {!isSale && anyShippedAndHeld ? (
            <button
              onClick={function(e) { e.stopPropagation(); self.confirmReceipt(sg.sellerKey); }}
              disabled={self.state.confirmingOrderId === sg.sellerKey}
              className="tw-px-5 tw-py-2.5 tw-rounded-lg tw-bg-green-600 tw-text-white tw-text-sm tw-font-semibold tw-border-none tw-cursor-pointer hover:tw-bg-green-700 tw-transition-colors disabled:tw-opacity-60 disabled:tw-cursor-not-allowed"
            >
              {self.state.confirmingOrderId === sg.sellerKey ? 'Confirming...' : 'Confirm Receipt'}
            </button>
          ) : null}
          {!isSale && anyShippedAndHeld ? (
            <button
              onClick={function(e) { e.stopPropagation(); self.openDispute(sg.sellerKey); }}
              className="tw-px-5 tw-py-2.5 tw-rounded-lg tw-bg-transparent tw-text-red-500 tw-text-sm tw-font-semibold tw-border tw-border-red-500/30 tw-cursor-pointer hover:tw-bg-red-500/10 tw-transition-colors"
            >
              Report Issue
            </button>
          ) : null}
          {!isSale && anyUnreviewed ? (
            <button
              onClick={function(e) {
                e.stopPropagation();
                var unreviewedItem = sg.items.find(function(item) { return !item.reviewed && (item.payoutStatus === 'released' || item.sellerPaid); });
                if (unreviewedItem) {
                  self.setState({ reviewOrderId: unreviewedItem._id, reviewRating: 0, reviewText: '', hoverRating: 0 });
                }
              }}
              className="tw-px-5 tw-py-2.5 tw-rounded-lg tw-bg-transparent tw-text-sm tw-font-semibold tw-border tw-cursor-pointer tw-transition-colors"
              style={{ color: '#d4a843', borderColor: 'rgba(212,168,67,0.3)' }}
              onMouseEnter={function(e) { e.target.style.backgroundColor = 'rgba(212,168,67,0.1)'; }}
              onMouseLeave={function(e) { e.target.style.backgroundColor = 'transparent'; }}
            >
              <i className="fa fa-star tw-mr-1.5"></i>Leave a Review
            </button>
          ) : null}
          {otherId ? (
            <button
              onClick={function(e) {
                e.stopPropagation();
                var pids = [];
                sg.items.forEach(function(item) { if (item.post && item.post._id) pids.push(item.post._id); });
                self.messageUser(otherId, pids, sg.sellerKey, isSale);
              }}
              disabled={self.state.messagingOrderId === sg.sellerKey}
              className="tw-px-5 tw-py-2.5 tw-rounded-lg tw-bg-transparent tw-text-sm tw-font-semibold tw-border tw-cursor-pointer tw-transition-colors tw-text-[#666] dark:tw-text-[#aaa] tw-border-[#e0e0e0] dark:tw-border-[#333] hover:tw-border-brand-gold hover:tw-text-brand-gold disabled:tw-opacity-60 disabled:tw-cursor-not-allowed"
            >
              <i className="fa fa-envelope tw-mr-1.5"></i>
              {self.state.messagingOrderId === sg.sellerKey ? 'Opening...' : (isSale ? 'Message Buyer' : 'Message Seller')}
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  renderOrderDetail(group, isSale, isDark) {
    var self = this;
    var isMulti = group.items.length > 1;
    var order = group.primary;
    var totals = getGroupTotal(group, isSale);
    var date = formatDate(order.createdAt);

    // Determine active seller tab for multi-seller orders
    var activeSellerKey = this.state.activeSellerTab[group.key] || (group.sellerGroups[0] && group.sellerGroups[0].sellerKey);
    var activeSellerGroup = null;
    for (var i = 0; i < group.sellerGroups.length; i++) {
      if (group.sellerGroups[i].sellerKey === activeSellerKey) {
        activeSellerGroup = group.sellerGroups[i];
        break;
      }
    }
    if (!activeSellerGroup) activeSellerGroup = group.sellerGroups[0];

    var statCardStyle = { backgroundColor: isDark ? '#111' : '#f5f5f5', border: isDark ? '1px solid #222' : '1px solid #eee', borderRadius: '8px', padding: '8px 12px' };
    var totalCardStyle = Object.assign({}, statCardStyle, { borderLeft: '3px solid #c9a84c' });

    return (
      <div
        style={{ padding: '20px 20px 20px 23px', borderTop: '1px solid rgba(212,168,67,0.2)', backgroundColor: isDark ? '#141414' : '#fff' }}
      >
        {/* Section 1 — Item List */}
        {group.items.map(function(item) {
          var itemPost = item.post || {};
          var itemImage = resolveCardImage(itemPost.cardImage) || resolveCardImage(itemPost.images && itemPost.images[0]) || '';
          var itemTitle = itemPost.title || 'Untitled';
          var itemPrice = item.itemPrice || itemPost.price || 0;
          var itemSeller = item.seller ? item.seller.userName : '';
          var itemSellerId = item.seller ? item.seller._id : '';
          var isHighlighted = group.isMultiSeller && !isSale && activeSellerGroup && activeSellerGroup.sellerId === itemSellerId;
          var itemHoverKey = '_itemHover_' + item._id;

          return (
            <div
              key={item._id}
              className="tw-flex tw-gap-3 tw-mb-3 tw-rounded-lg"
              onMouseEnter={function() { self[itemHoverKey] = true; self.forceUpdate(); }}
              onMouseLeave={function() { self[itemHoverKey] = false; self.forceUpdate(); }}
              style={isHighlighted
                ? { backgroundColor: 'rgba(59,130,246,0.06)', borderLeft: '3px solid #3b82f6', padding: '8px 8px 8px 10px', margin: '0 0 8px -13px', transition: 'background-color 0.15s ease' }
                : { padding: '4px 6px', opacity: group.isMultiSeller && !isSale ? 0.5 : 1, backgroundColor: self[itemHoverKey] ? (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)') : 'transparent', borderRadius: '8px', transition: 'background-color 0.15s ease' }
              }
            >
              {itemImage ? (
                <Link to={'/product/' + itemPost._id} className="tw-flex-shrink-0">
                  <img
                    src={itemImage}
                    alt={itemTitle}
                    className="tw-rounded"
                    style={{ width: isMulti ? '60px' : '72px', height: isMulti ? '84px' : '100px', objectFit: 'cover', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                  />
                </Link>
              ) : (
                <div style={{ width: isMulti ? '60px' : '72px', height: isMulti ? '84px' : '100px', flexShrink: 0, borderRadius: '6px', backgroundColor: isDark ? '#0f0f0f' : '#f5f5f5' }}></div>
              )}
              <div className="tw-flex-1 tw-min-w-0">
                <Link to={'/product/' + (itemPost._id || '')} className="tw-text-sm tw-font-semibold hover:tw-text-brand-gold tw-transition-colors tw-block tw-no-underline tw-truncate" style={{ color: isDark ? '#fff' : '#111' }}>
                  {itemTitle}
                </Link>
                {itemPost.set ? (
                  <p className="tw-text-xs tw-mb-0 tw-mt-0.5" style={{ color: '#888' }}>{itemPost.set}</p>
                ) : null}
                <div className="tw-flex tw-items-center tw-gap-2 tw-mt-1">
                  <p className="tw-text-sm tw-font-semibold tw-mb-0" style={{ color: isDark ? '#fff' : '#111' }}>${itemPrice.toFixed(2)}</p>
                  {group.isMultiSeller && !isSale && itemSeller ? (
                    <span className="tw-text-xs" style={{ color: '#999' }}>from {itemSeller}</span>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}

        {/* Divider before totals */}
        <div style={{ borderTop: isDark ? '1px solid #2a2a2a' : '1px solid #e5e5e5', margin: '12px 0 12px 0' }}></div>

        {/* Order summary — combined totals */}
        <div className="tw-mb-4">
          <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap tw-mt-2">
            <div style={statCardStyle}>
              <span className="tw-text-xs" style={{ color: '#999' }}>{isMulti ? 'Items (' + group.items.length + ')' : 'Item'}</span>
              <p className="tw-text-sm tw-font-semibold tw-mb-0" style={{ color: isDark ? '#fff' : '#111' }}>${totals.itemTotal.toFixed(2)}</p>
            </div>
            {totals.shipping > 0 ? (
              <div style={statCardStyle}>
                <span className="tw-text-xs" style={{ color: '#999' }}>Shipping</span>
                <p className="tw-text-sm tw-font-medium tw-mb-0" style={{ color: isDark ? '#fff' : '#111' }}>${totals.shipping.toFixed(2)}</p>
              </div>
            ) : (
              <div style={statCardStyle}>
                <span className="tw-text-xs" style={{ color: '#999' }}>Shipping</span>
                <p className="tw-text-sm tw-font-medium tw-text-green-600 tw-mb-0">Free</p>
              </div>
            )}
            {totals.trackingFees > 0 ? (
              <div style={statCardStyle}>
                <span className="tw-text-xs" style={{ color: '#999' }}>Tracking Upgrade</span>
                <p className="tw-text-sm tw-font-medium tw-mb-0" style={{ color: isDark ? '#fff' : '#111' }}>${totals.trackingFees.toFixed(2)}</p>
              </div>
            ) : null}
            {!isSale && totals.transactionFee > 0 ? (
              <div style={statCardStyle}>
                <span className="tw-text-xs" style={{ color: '#999' }}>Tx Fee</span>
                <p className="tw-text-sm tw-font-medium tw-mb-0" style={{ color: isDark ? '#fff' : '#111' }}>${totals.transactionFee.toFixed(2)}</p>
              </div>
            ) : null}
            {!isSale && totals.taxTotal > 0 ? (
              <div style={statCardStyle}>
                <span className="tw-text-xs" style={{ color: '#999' }}>Tax</span>
                <p className="tw-text-sm tw-font-medium tw-mb-0" style={{ color: isDark ? '#fff' : '#111' }}>${totals.taxTotal.toFixed(2)}</p>
              </div>
            ) : null}
            {isSale && totals.payoutTotal > 0 ? (
              <div style={statCardStyle}>
                <span className="tw-text-xs" style={{ color: '#999' }}>Platform Fee</span>
                <p className="tw-text-sm tw-font-medium tw-mb-0" style={{ color: '#ef4444' }}>-${(totals.total - totals.payoutTotal).toFixed(2)}</p>
              </div>
            ) : null}
            {isSale && totals.payoutTotal > 0 ? (
              <div style={Object.assign({}, totalCardStyle, { borderLeft: '3px solid #16a34a' })}>
                <span className="tw-text-xs" style={{ color: '#999' }}>Your Payout</span>
                <p className="tw-text-sm tw-font-bold tw-mb-0" style={{ color: '#16a34a' }}>${totals.payoutTotal.toFixed(2)}</p>
              </div>
            ) : (
              <div style={totalCardStyle}>
                <span className="tw-text-xs" style={{ color: '#999' }}>Total</span>
                <p className="tw-text-sm tw-font-bold tw-mb-0" style={{ color: isDark ? '#fff' : '#111' }}>${totals.total.toFixed(2)}</p>
              </div>
            )}
          </div>
          {isSale && totals.payoutTotal > 0 ? (
            <p className="tw-text-[10px] tw-mt-1.5 tw-mb-0" style={{ color: '#999' }}>
              <i className="fa fa-info-circle tw-mr-1"></i>
              {totals.itemTotal < 9 ? (
                <span>You keep 90% of your item price{totals.shipping > 0 ? ' + all shipping' : ''}. On orders under $9, your payout may be slightly lower to cover processing costs. <Link to="/faq#selling" className="tw-no-underline" style={{ color: '#c9a84c' }}>Learn more</Link></span>
              ) : (
                <span>You keep 90% of your item price{totals.shipping > 0 ? ' + all shipping' : ''}.</span>
              )}
            </p>
          ) : null}
          <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap tw-mt-2">
            <span className="tw-text-xs tw-text-[#999]">
              {date}
            </span>
            <span className="tw-text-xs tw-text-[#888]" style={{ fontFamily: 'monospace', letterSpacing: '0.5px' }}>
              Order #{order._id ? order._id.slice(-8).toUpperCase() : ''}
            </span>
            {order.paymentMethod && !isSale ? (
              <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-[10px] tw-font-medium tw-px-2 tw-py-0.5 tw-rounded-full tw-bg-[#f0f0f0] dark:tw-bg-[#222] tw-text-[#666] dark:tw-text-[#aaa]">
                <i className={'fa ' + (order.paymentMethod === 'paypal' ? 'fa-paypal' : 'fa-credit-card') + ' tw-text-[10px]'}
                  style={{ color: order.paymentMethod === 'paypal' ? '#003087' : '#635bff' }}
                ></i>
                {order.paymentMethod === 'paypal' ? 'PayPal' : (
                  order.cardBrand && order.cardLast4
                    ? (order.cardBrand.charAt(0).toUpperCase() + order.cardBrand.slice(1) + ' \u2022\u2022\u2022\u2022 ' + order.cardLast4)
                    : 'Card'
                )}
              </span>
            ) : null}
          </div>
        </div>

        {/* Divider before seller content */}
        <div className="tw-border-t tw-border-[#e5e5e5] dark:tw-border-[#2a2a2a] tw-my-4"></div>

        {/* Multi-seller tabs OR single-seller inline content */}
        {group.isMultiSeller && !isSale ? (
          <div>
            {/* Seller tabs */}
            <div className="tw-flex tw-gap-2 tw-flex-wrap tw-mb-4">
              {group.sellerGroups.map(function(sg) {
                var sgStatus = getGroupStatus(sg, isSale);
                var sgStatusLabel = getStatusLabel(sgStatus);
                var isActive = sg.sellerKey === activeSellerGroup.sellerKey;
                return (
                  <button
                    key={sg.sellerKey}
                    onClick={function(e) {
                      e.stopPropagation();
                      var tabs = Object.assign({}, self.state.activeSellerTab);
                      tabs[group.key] = sg.sellerKey;
                      self.setState({ activeSellerTab: tabs });
                    }}
                    className="tw-border-none tw-cursor-pointer tw-text-xs tw-font-medium tw-rounded-full tw-px-3 tw-py-1.5"
                    style={isActive
                      ? { backgroundColor: '#d4a843', color: '#fff', boxShadow: '0 2px 8px rgba(212,168,67,0.3)', transition: 'all 0.2s ease' }
                      : { backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', color: '#888', transition: 'all 0.2s ease' }
                    }
                  >
                    {sg.sellerName} &middot; {sgStatusLabel}
                  </button>
                );
              })}
            </div>
            {this.renderSellerTabContent(activeSellerGroup, group, isSale)}
          </div>
        ) : (
          this.renderSellerTabContent(group.sellerGroups[0], group, isSale)
        )}
      </div>
    );
  }

  render() {
    var self = this;
    var isDark = document.documentElement.classList.contains('tw-dark');
    var isEarnings = this.state.subTab === 'earnings';
    var isSale = this.state.subTab === 'sales';

    // For earnings, use raw array; for purchases/sales, use grouped arrays
    var groups;
    if (isEarnings) {
      groups = this.state.earnings;
    } else {
      groups = isSale ? this.state.saleGroups : this.state.purchaseGroups;
    }

    // Filter groups by status (not for earnings tab)
    if (!isEarnings && this.state.statusFilter !== 'all') {
      groups = groups.filter(function(group) {
        var gs = getGroupStatus(group, isSale);
        var f = self.state.statusFilter;
        // "Partially Shipped" should appear under both awaiting and shipped filters
        if (gs === 'partially_shipped') return f === 'awaiting' || f === 'shipped';
        return gs === f;
      });
    }

    // Pagination
    var totalPages = Math.ceil(groups.length / ITEMS_PER_PAGE);
    var start = this.state.page * ITEMS_PER_PAGE;
    var pageGroups = groups.slice(start, start + ITEMS_PER_PAGE);

    var subTabStyle = function(key) {
      var active = self.state.subTab === key;
      return {
        padding: '8px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: 500,
        border: 'none', cursor: 'pointer', transition: 'all 0.2s ease',
        backgroundColor: active ? '#c9a84c' : (isDark ? '#0f0f0f' : '#f5f5f5'),
        color: active ? '#fff' : (isDark ? '#999' : '#666'),
        boxShadow: active ? '0 2px 8px rgba(201,168,76,0.3)' : 'none',
      };
    };

    if (this.state.loading) {
      return (
        <div className="tw-flex tw-items-center tw-justify-center tw-py-12">
          <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-t-2 tw-border-b-2 tw-border-brand-gold"></div>
        </div>
      );
    }

    return (
      <div>
        {/* Page Header */}
        <div className="tw-mb-5">
          <h1 className="tw-text-2xl tw-font-bold tw-mb-1" style={{ color: isDark ? '#fff' : '#111' }}>Your Orders</h1>
          <p className="tw-text-sm tw-mb-0" style={{ color: '#888' }}>Track purchases, manage sales, and view referral earnings</p>
        </div>
        <div className="tw-rounded-xl tw-p-6 tw-mb-6" style={{
          backgroundColor: isDark ? '#1a1a1a' : '#fff',
          border: isDark ? '1px solid #2a2a2a' : '1px solid #e8e8e8',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.04)',
        }}>
          {/* Sub-tabs */}
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-6 tw-flex-wrap tw-gap-3">
            <div className="tw-flex tw-gap-2">
              <button
                onClick={function() { self.setState({ subTab: 'purchases', page: 0, expandedOrderId: null, activeSellerTab: {} }); }}
                style={subTabStyle('purchases')}
              >
                Purchases ({this.state.purchaseGroups.length})
              </button>
              <button
                onClick={function() { self.setState({ subTab: 'sales', page: 0, expandedOrderId: null, activeSellerTab: {} }); }}
                style={subTabStyle('sales')}
              >
                Sales ({this.state.saleGroups.length})
              </button>
              <button
                onClick={function() { self.setState({ subTab: 'earnings', page: 0, expandedOrderId: null, activeSellerTab: {} }); }}
                style={subTabStyle('earnings')}
              >
                Referrals ({this.state.earnings.length})
              </button>
            </div>

            {/* Status Filter */}
            {!isEarnings ? (
              <select
                value={this.state.statusFilter}
                onChange={function(e) { self.setState({ statusFilter: e.target.value, page: 0, expandedOrderId: null, activeSellerTab: {} }); }}
                className="tw-rounded-lg tw-px-4 tw-py-2.5 tw-text-sm tw-outline-none"
                style={{ backgroundColor: isDark ? '#0f0f0f' : '#f5f5f5', border: isDark ? '1px solid #333' : '1px solid #e0e0e0', color: isDark ? '#fff' : '#111' }}
              >
                <option value="all">All Orders</option>
                <option value="awaiting">Awaiting Shipment</option>
                <option value="shipped">Shipped</option>
                <option value="completed">Completed</option>
                {isSale ? <option value="payout_failed">Payout Pending</option> : null}
                {isSale ? <option value="awaiting_stripe_connect">Connect Stripe</option> : null}
                <option value="disputed">Disputed</option>
              </select>
            ) : null}
          </div>

          {/* Active filter chip */}
          {!isEarnings && self.state.statusFilter !== 'all' ? (
            <div className="tw-mb-4 tw-flex tw-items-center tw-gap-2">
              <span className="tw-text-xs tw-font-medium tw-px-3 tw-py-1 tw-rounded-full tw-inline-flex tw-items-center tw-gap-1.5" style={{ backgroundColor: 'rgba(201,168,76,0.1)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.3)' }}>
                {getStatusLabel(self.state.statusFilter)}
                <button
                  onClick={function() { self.setState({ statusFilter: 'all', page: 0, expandedOrderId: null }); }}
                  className="tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 tw-ml-1 tw-text-xs"
                  style={{ color: '#c9a84c', lineHeight: 1 }}
                >
                  <i className="fa fa-times"></i>
                </button>
              </span>
              <span className="tw-text-xs" style={{ color: '#888' }}>{groups.length} order{groups.length !== 1 ? 's' : ''}</span>
            </div>
          ) : null}

          {/* Unclaimed payouts banner */}
          {isSale && self.state.unclaimedPayouts > 0 ? (
            <div className="tw-mb-4 tw-p-4 tw-rounded-xl tw-flex tw-items-center tw-justify-between tw-gap-3 tw-flex-wrap" style={{ backgroundColor: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)' }}>
              <div className="tw-flex tw-items-center tw-gap-3">
                <i className="fa fa-exclamation-circle tw-text-lg" style={{ color: '#f59e0b' }}></i>
                <div>
                  <p className="tw-text-sm tw-font-semibold tw-text-[#111] dark:tw-text-white tw-mb-0">You have {self.state.unclaimedPayouts} unclaimed payout{self.state.unclaimedPayouts !== 1 ? 's' : ''}</p>
                  <p className="tw-text-xs tw-text-[#666] dark:tw-text-[#aaa] tw-mb-0">Set up a payout method to receive your earnings.</p>
                </div>
              </div>
              <Link
                to="/settings"
                className="tw-inline-block tw-text-sm tw-font-semibold tw-px-4 tw-py-2 tw-rounded-lg tw-no-underline tw-transition-colors"
                style={{ backgroundColor: 'rgba(245,158,11,0.15)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)' }}
              >
                Set Up Payments
              </Link>
            </div>
          ) : null}

          {/* Connect Stripe banner — shown to sellers who have no Stripe Connect when flag is off */}
          {isSale && !PAYPAL_PAYOUTS_ENABLED && !(function() { var p = Auth.getProfile(); return p && p.stripeAccountId; })() ? (
            <div className="tw-mb-4 tw-p-4 tw-rounded-xl tw-flex tw-items-center tw-justify-between tw-gap-3 tw-flex-wrap" style={{ backgroundColor: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)' }}>
              <div className="tw-flex tw-items-center tw-gap-3">
                <i className="fa fa-exclamation-triangle tw-text-lg" style={{ color: '#f59e0b' }}></i>
                <div>
                  <p className="tw-text-sm tw-font-semibold tw-text-[#111] dark:tw-text-white tw-mb-0">Connect Stripe to receive your payouts</p>
                  <p className="tw-text-xs tw-text-[#666] dark:tw-text-[#aaa] tw-mb-0">Once connected, held funds will be released to your Stripe account automatically.</p>
                </div>
              </div>
              <Link
                to="/settings?tab=payments"
                className="tw-inline-block tw-text-sm tw-font-semibold tw-px-4 tw-py-2 tw-rounded-lg tw-no-underline tw-transition-colors"
                style={{ backgroundColor: 'rgba(245,158,11,0.15)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)' }}
              >
                Connect Stripe
              </Link>
            </div>
          ) : null}

          {/* Sales Revenue Summary */}
          {isSale && self.state.sales.length > 0 ? (function() {
            var now = new Date();
            var period = self.state.revenuePeriod;

            // Compute period start date
            var periodStart;
            var periodLabel;
            if (period === 'day') {
              periodStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
              periodLabel = 'Today';
            } else if (period === 'week') {
              periodStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
              periodStart.setDate(periodStart.getDate() - periodStart.getDay());
              periodLabel = 'This Week';
            } else if (period === 'month') {
              periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
              periodLabel = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
            } else if (period === 'year') {
              periodStart = new Date(now.getFullYear(), 0, 1);
              periodLabel = String(now.getFullYear());
            } else {
              // Specific year like "2025"
              var yr = Number(period);
              periodStart = new Date(yr, 0, 1);
              var periodEnd = new Date(yr + 1, 0, 1);
              periodLabel = String(yr);
            }

            var periodReleased = 0, periodPending = 0, periodUnclaimed = 0, periodEarned = 0, periodSaleCount = 0;

            self.state.sales.forEach(function(sale) {
              var payout = Number(sale.sellerPayout) || 0;
              if (sale.payoutStatus === 'refunded' || sale.payoutStatus === 'disputed') return;
              var saleDate = new Date(sale.createdAt);
              var inPeriod = false;
              if (period !== 'day' && period !== 'week' && period !== 'month' && period !== 'year') {
                var yr2 = Number(period);
                inPeriod = saleDate >= new Date(yr2, 0, 1) && saleDate < new Date(yr2 + 1, 0, 1);
              } else {
                inPeriod = saleDate >= periodStart;
              }
              if (inPeriod) {
                periodEarned += payout;
                periodSaleCount++;
                if (sale.sellerPaid || sale.payoutStatus === 'released') {
                  periodReleased += payout;
                } else if (sale.payoutStatus === 'payout_failed') {
                  periodUnclaimed += payout;
                } else {
                  periodPending += payout;
                }
              }
            });

            // Build list of years that have sales for the year selector
            var yearsWithSales = {};
            self.state.sales.forEach(function(sale) {
              var y = new Date(sale.createdAt).getFullYear();
              yearsWithSales[y] = true;
            });
            var yearOptions = Object.keys(yearsWithSales).sort().reverse();

            var periodBtnStyle = function(key) {
              var active = self.state.revenuePeriod === key;
              return {
                padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 500,
                border: 'none', cursor: 'pointer', transition: 'all 0.2s ease',
                backgroundColor: active ? '#c9a84c' : (isDark ? '#1a1a1a' : '#f0f0f0'),
                color: active ? '#fff' : '#888',
                boxShadow: active ? '0 2px 8px rgba(201,168,76,0.3)' : 'none',
              };
            };

            return (
              <div className="tw-mb-6">
                {/* Period selector */}
                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3 tw-flex-wrap">
                  <button onClick={function() { self.setState({ revenuePeriod: 'day' }); }} style={periodBtnStyle('day')}>Today</button>
                  <button onClick={function() { self.setState({ revenuePeriod: 'week' }); }} style={periodBtnStyle('week')}>Week</button>
                  <button onClick={function() { self.setState({ revenuePeriod: 'month' }); }} style={periodBtnStyle('month')}>Month</button>
                  <button onClick={function() { self.setState({ revenuePeriod: 'year' }); }} style={periodBtnStyle('year')}>Year</button>
                  {yearOptions.length > 1 ? (
                    <select
                      value={['day','week','month','year'].indexOf(self.state.revenuePeriod) === -1 ? self.state.revenuePeriod : ''}
                      onChange={function(e) { if (e.target.value) self.setState({ revenuePeriod: e.target.value }); }}
                      className="tw-bg-[#f0f0f0] dark:tw-bg-[#1a1a1a] tw-rounded-md tw-px-2 tw-py-1.5 tw-text-xs tw-font-medium tw-text-[#888] tw-border-none tw-outline-none tw-cursor-pointer"
                    >
                      <option value="">By Year</option>
                      {yearOptions.map(function(y) { return <option key={y} value={y}>{y}</option>; })}
                    </select>
                  ) : null}
                </div>
                {/* Revenue cards */}
                <div className="tw-flex tw-gap-3 tw-flex-wrap">
                  <div className="tw-flex-1 tw-min-w-[120px] tw-rounded-xl tw-p-4 tw-text-center tw-cursor-default" style={{ backgroundColor: isDark ? '#111' : '#f9f9f9', border: isDark ? '1px solid #222' : '1px solid #eee', transition: 'transform 200ms ease, box-shadow 200ms ease' }}
                    onMouseEnter={function(e) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; }}
                    onMouseLeave={function(e) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <p className="tw-text-[10px] tw-font-medium tw-uppercase tw-tracking-wide tw-mb-1" style={{ color: '#999' }}>{periodLabel}</p>
                    <p className="tw-text-2xl tw-font-bold tw-mb-0" style={{ color: isDark ? '#fff' : '#111' }}>${periodEarned.toFixed(2)}</p>
                    <p className="tw-text-[10px] tw-mt-1 tw-mb-0" style={{ color: '#bbb' }}>{periodSaleCount} sale{periodSaleCount !== 1 ? 's' : ''}</p>
                  </div>
                  <div className="tw-flex-1 tw-min-w-[120px] tw-rounded-xl tw-p-4 tw-text-center tw-cursor-default" style={{ backgroundColor: 'rgba(22,163,74,0.1)', border: '1px solid rgba(22,163,74,0.2)', transition: 'transform 200ms ease, box-shadow 200ms ease' }}
                    onMouseEnter={function(e) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(22,163,74,0.15)'; }}
                    onMouseLeave={function(e) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <p className="tw-text-[10px] tw-text-green-600 tw-font-medium tw-uppercase tw-tracking-wide tw-mb-1">Released</p>
                    <p className="tw-text-2xl tw-font-bold tw-text-green-600 tw-mb-0">${periodReleased.toFixed(2)}</p>
                  </div>
                  <div className="tw-flex-1 tw-min-w-[120px] tw-rounded-xl tw-p-4 tw-text-center tw-cursor-default" style={{ backgroundColor: 'rgba(202,138,4,0.1)', border: '1px solid rgba(202,138,4,0.2)', transition: 'transform 200ms ease, box-shadow 200ms ease' }}
                    onMouseEnter={function(e) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(202,138,4,0.15)'; }}
                    onMouseLeave={function(e) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <p className="tw-text-[10px] tw-font-medium tw-uppercase tw-tracking-wide tw-mb-1" style={{ color: '#ca8a04' }}>Pending</p>
                    <p className="tw-text-2xl tw-font-bold tw-mb-0" style={{ color: '#ca8a04' }}>${periodPending.toFixed(2)}</p>
                  </div>
                  {periodUnclaimed > 0 ? (
                    <div className="tw-flex-1 tw-min-w-[120px] tw-rounded-xl tw-p-4 tw-text-center tw-cursor-default" style={{ backgroundColor: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)', transition: 'transform 200ms ease, box-shadow 200ms ease' }}
                      onMouseEnter={function(e) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(249,115,22,0.15)'; }}
                      onMouseLeave={function(e) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                    >
                      <p className="tw-text-[10px] tw-font-medium tw-uppercase tw-tracking-wide tw-mb-1 tw-text-orange-500">Unclaimed</p>
                      <p className="tw-text-2xl tw-font-bold tw-mb-0 tw-text-orange-500">${periodUnclaimed.toFixed(2)}</p>
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })() : null}

          {/* Referrals Summary */}
          {isEarnings ? (function() {
            var totalEarned = 0;
            var totalPending = 0;
            var totalUnclaimed = 0;
            self.state.earnings.forEach(function(e) {
              var amt = Number(e.marketerPayout) || 0;
              if (e.marketerPaid) {
                totalEarned += amt;
              } else if (e.marketerPayoutDeferred) {
                totalUnclaimed += amt;
              } else if (e.payoutStatus !== 'refunded' && e.payoutStatus !== 'disputed') {
                totalPending += amt;
              }
            });
            return (
              <div>
                {totalUnclaimed > 0 ? (
                  <div className="tw-rounded-xl tw-p-4 tw-mb-4 tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3" style={{ backgroundColor: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)' }}>
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <i className="fa fa-exclamation-circle tw-text-orange-500"></i>
                      <span className="tw-text-sm tw-text-orange-600 dark:tw-text-orange-400">
                        You have <strong>${totalUnclaimed.toFixed(2)}</strong> in unclaimed commissions.
                      </span>
                    </div>
                    <Link to="/settings" className="tw-text-xs tw-font-semibold tw-px-3 tw-py-1.5 tw-rounded-lg tw-no-underline tw-text-white tw-bg-orange-500 hover:tw-bg-orange-600 tw-transition-colors">
                      Connect Payout Method
                    </Link>
                  </div>
                ) : null}
                <div className="tw-flex tw-gap-4 tw-mb-6 tw-flex-wrap">
                  <div className="tw-flex-1 tw-min-w-[120px] tw-bg-green-600/10 tw-rounded-xl tw-p-4 tw-text-center" style={{ border: '1px solid rgba(22,163,74,0.2)' }}>
                    <p className="tw-text-xs tw-text-green-600 tw-font-medium tw-mb-1">Total Earned</p>
                    <p className="tw-text-2xl tw-font-bold tw-text-green-600 tw-mb-0">${totalEarned.toFixed(2)}</p>
                  </div>
                  <div className="tw-flex-1 tw-min-w-[120px] tw-rounded-xl tw-p-4 tw-text-center" style={{ backgroundColor: 'rgba(202,138,4,0.1)', border: '1px solid rgba(202,138,4,0.2)' }}>
                    <p className="tw-text-xs tw-font-medium tw-mb-1" style={{ color: '#ca8a04' }}>Pending</p>
                    <p className="tw-text-2xl tw-font-bold tw-mb-0" style={{ color: '#ca8a04' }}>${totalPending.toFixed(2)}</p>
                  </div>
                  {totalUnclaimed > 0 ? (
                    <div className="tw-flex-1 tw-min-w-[120px] tw-rounded-xl tw-p-4 tw-text-center" style={{ backgroundColor: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.2)' }}>
                      <p className="tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-mb-1 tw-text-orange-500">Unclaimed</p>
                      <p className="tw-text-2xl tw-font-bold tw-mb-0 tw-text-orange-500">${totalUnclaimed.toFixed(2)}</p>
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })() : null}

          {/* Orders List */}
          {pageGroups.length > 0 ? (
            <div>
              {pageGroups.map(function(group) {
                if (isEarnings) {
                  return self.renderEarningsRow(group);
                }
                return self.renderOrderRow(group, isSale);
              })}
            </div>
          ) : (
            <div className="tw-text-center tw-py-16">
              <i className={'fa tw-text-5xl tw-mb-4 ' + (isEarnings ? 'fa-money' : 'fa-shopping-bag')} style={{ display: 'block', color: isDark ? '#444' : '#ccc' }}></i>
              <p className="tw-text-sm tw-mb-2" style={{ color: '#888' }}>{isEarnings ? 'No referral earnings yet. Share other sellers\' listings to earn commissions!' : (self.state.statusFilter !== 'all' ? 'No orders match this filter' : 'No orders found')}</p>
              {isEarnings ? (
                <a href="/sharingmore" className="tw-text-sm tw-font-medium" style={{ color: '#c9a84c' }}>Learn how Share & Earn works</a>
              ) : null}
              {!isEarnings && self.state.statusFilter !== 'all' ? (
                <button
                  onClick={function() { self.setState({ statusFilter: 'all', page: 0 }); }}
                  className="tw-text-sm tw-font-medium tw-bg-transparent tw-border-none tw-cursor-pointer tw-mt-1"
                  style={{ color: '#c9a84c' }}
                >
                  Clear filter
                </button>
              ) : null}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 ? (
            <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-mt-6">
              <button
                onClick={function() { self.setState({ page: Math.max(0, self.state.page - 1), expandedOrderId: null, activeSellerTab: {} }); }}
                disabled={this.state.page === 0}
                className="tw-px-3 tw-py-2 tw-rounded-lg tw-text-sm tw-cursor-pointer disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                style={{ border: isDark ? '1px solid #333' : '1px solid #e0e0e0', backgroundColor: 'transparent', color: isDark ? '#999' : '#666', transition: 'background-color 0.15s ease' }}
                onMouseEnter={function(e) { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = isDark ? '#222' : '#f0f0f0'; }}
                onMouseLeave={function(e) { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <i className="fa fa-chevron-left"></i>
              </button>
              <span className="tw-text-sm" style={{ color: '#888' }}>
                Page {this.state.page + 1} of {totalPages}
              </span>
              <button
                onClick={function() { self.setState({ page: Math.min(totalPages - 1, self.state.page + 1), expandedOrderId: null, activeSellerTab: {} }); }}
                disabled={this.state.page >= totalPages - 1}
                className="tw-px-3 tw-py-2 tw-rounded-lg tw-text-sm tw-cursor-pointer disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                style={{ border: isDark ? '1px solid #333' : '1px solid #e0e0e0', backgroundColor: 'transparent', color: isDark ? '#999' : '#666', transition: 'background-color 0.15s ease' }}
                onMouseEnter={function(e) { if (!e.currentTarget.disabled) e.currentTarget.style.backgroundColor = isDark ? '#222' : '#f0f0f0'; }}
                onMouseLeave={function(e) { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <i className="fa fa-chevron-right"></i>
              </button>
            </div>
          ) : null}
        </div>

        {/* Dispute Modal */}
        {this.state.disputeOrderId ? (
          <div className="tw-fixed tw-inset-0 tw-bg-black/50 tw-flex tw-items-center tw-justify-center tw-z-50" style={{ backdropFilter: 'blur(4px)' }} onClick={function(e) { if (e.target === e.currentTarget) self.setState({ disputeOrderId: null }); }}>
            <div className="modal-enter tw-rounded-xl tw-p-6 tw-w-full tw-max-w-md tw-mx-4" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: isDark ? '1px solid #2a2a2a' : '1px solid #e0e0e0' }}>
              <h3 className="tw-text-lg tw-font-bold tw-text-[#111] dark:tw-text-white tw-mb-4">Report an Issue</h3>
              <div className="tw-mb-4">
                <label className="tw-text-sm tw-font-medium tw-text-[#555] dark:tw-text-[#aaa] tw-mb-2 tw-block">Reason</label>
                <select
                  value={this.state.disputeReason}
                  onChange={function(e) { self.setState({ disputeReason: e.target.value }); }}
                  className="tw-w-full tw-bg-[#f5f5f5] dark:tw-bg-[#0f0f0f] tw-rounded-lg tw-px-4 tw-py-2.5 tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-text-sm tw-text-[#111] dark:tw-text-white tw-outline-none"
                >
                  <option value="">Select a reason...</option>
                  <option value="Item not received">Item not received</option>
                  <option value="Item not as described">Item not as described</option>
                  <option value="Item damaged">Item damaged</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="tw-mb-4">
                <label className="tw-text-sm tw-font-medium tw-text-[#555] dark:tw-text-[#aaa] tw-mb-2 tw-block">Description (optional)</label>
                <textarea
                  value={this.state.disputeDescription}
                  onChange={function(e) { self.setState({ disputeDescription: e.target.value }); }}
                  placeholder="Describe the issue..."
                  rows={3}
                  className="tw-w-full tw-bg-[#f5f5f5] dark:tw-bg-[#0f0f0f] tw-rounded-lg tw-px-3 tw-py-2.5 tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-text-sm tw-text-[#111] dark:tw-text-white tw-outline-none tw-resize-none"
                />
              </div>
              <div className="tw-flex tw-gap-3 tw-justify-end">
                <button
                  onClick={function() { self.setState({ disputeOrderId: null }); }}
                  className="tw-px-4 tw-py-2 tw-rounded-lg tw-text-sm tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-bg-transparent tw-text-[#666] dark:tw-text-[#999] tw-cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={this.submitDispute}
                  disabled={this.state.submittingDispute}
                  className="tw-px-4 tw-py-2 tw-rounded-lg tw-bg-red-500 tw-text-white tw-text-sm tw-font-medium tw-border-none tw-cursor-pointer hover:tw-bg-red-600 tw-transition-colors disabled:tw-opacity-60 disabled:tw-cursor-not-allowed"
                >
                  {this.state.submittingDispute ? 'Submitting...' : 'Submit Dispute'}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {/* Review Modal */}
        {this.state.reviewOrderId ? (function() {
          var reviewOrder = self.state.purchases.find(function(p) { return p._id === self.state.reviewOrderId; });
          var orderTitle = reviewOrder && reviewOrder.post ? reviewOrder.post.title : 'this item';
          var sellerName = reviewOrder && reviewOrder.seller ? reviewOrder.seller.userName : 'the seller';

          return (
            <div className="tw-fixed tw-inset-0 tw-bg-black/50 tw-flex tw-items-center tw-justify-center tw-z-50" style={{ backdropFilter: 'blur(4px)' }} onClick={function(e) { if (e.target === e.currentTarget) self.setState({ reviewOrderId: null }); }}>
              <div className="modal-enter tw-rounded-xl tw-p-6 tw-w-full tw-max-w-md tw-mx-4" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: isDark ? '1px solid #2a2a2a' : '1px solid #e0e0e0' }}>
                <h3 className="tw-text-lg tw-font-bold tw-text-[#111] dark:tw-text-white tw-mb-1">Leave a Review</h3>
                <p className="tw-text-sm tw-text-[#888] tw-mb-5">How was your experience with <span className="tw-text-[#111] dark:tw-text-white tw-font-medium">{sellerName}</span> for <span className="tw-text-[#111] dark:tw-text-white tw-font-medium">{orderTitle}</span>?</p>

                {/* Star Rating */}
                <div className="tw-mb-4">
                  <label className="tw-text-sm tw-font-medium tw-text-[#555] dark:tw-text-[#aaa] tw-mb-2 tw-block">Rating</label>
                  <div className="tw-flex tw-gap-1">
                    {[1, 2, 3, 4, 5].map(function(star) {
                      var filled = star <= (self.state.hoverRating || self.state.reviewRating);
                      return (
                        <button
                          key={star}
                          type="button"
                          onClick={function() { self.setState({ reviewRating: star }); }}
                          onMouseEnter={function() { self.setState({ hoverRating: star }); }}
                          onMouseLeave={function() { self.setState({ hoverRating: 0 }); }}
                          className="tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-1 hover:tw-scale-110"
                          style={{ fontSize: '28px', color: filled ? '#f59e0b' : '#d1d5db', lineHeight: 1, transition: 'color 150ms ease, transform 150ms ease' }}
                        >
                          <i className={filled ? 'fa fa-star' : 'fa fa-star-o'}></i>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Review Text */}
                <div className="tw-mb-5">
                  <label className="tw-text-sm tw-font-medium tw-text-[#555] dark:tw-text-[#aaa] tw-mb-2 tw-block">Review <span className="tw-font-normal tw-text-[#bbb]">(optional)</span></label>
                  <textarea
                    value={self.state.reviewText}
                    onChange={function(e) { self.setState({ reviewText: e.target.value }); }}
                    placeholder="How was the card condition, packaging, shipping speed?"
                    rows={3}
                    className="tw-w-full tw-bg-[#f5f5f5] dark:tw-bg-[#0f0f0f] tw-rounded-lg tw-px-3 tw-py-2.5 tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-text-sm tw-text-[#111] dark:tw-text-white tw-outline-none tw-resize-none"
                  />
                </div>

                <div className="tw-flex tw-gap-3 tw-justify-end">
                  <button
                    onClick={function() { self.setState({ reviewOrderId: null }); }}
                    className="tw-px-4 tw-py-2 tw-rounded-lg tw-text-sm tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-bg-transparent tw-text-[#666] dark:tw-text-[#999] tw-cursor-pointer"
                  >
                    Skip
                  </button>
                  <button
                    onClick={self.submitReview}
                    disabled={self.state.submittingReview}
                    className="tw-px-4 tw-py-2 tw-rounded-lg tw-bg-brand-gold tw-text-white tw-text-sm tw-font-medium tw-border-none tw-cursor-pointer hover:tw-bg-brand-gold/90 tw-transition-colors disabled:tw-opacity-60 disabled:tw-cursor-not-allowed"
                  >
                    {self.state.submittingReview ? 'Submitting...' : 'Submit Review'}
                  </button>
                </div>
              </div>
            </div>
          );
        })() : null}

        {/* Message Modal */}
        {this.state.messageModalOtherId ? (
          <div className="tw-fixed tw-inset-0 tw-bg-black/50 tw-flex tw-items-center tw-justify-center tw-z-50" style={{ backdropFilter: 'blur(4px)' }} onClick={function(e) { if (e.target === e.currentTarget) self.setState({ messageModalOtherId: null }); }}>
            <div className="modal-enter tw-rounded-xl tw-p-6 tw-w-full tw-max-w-md tw-mx-4 tw-shadow-xl" style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff' }}>
              <h3 className="tw-text-base tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-1 tw-m-0">
                <i className="fa fa-envelope tw-mr-2 tw-text-brand-gold"></i>
                {this.state.messageModalIsSale ? 'Message Buyer' : 'Message Seller'}
              </h3>
              <p className="tw-text-xs tw-text-[#888] dark:tw-text-[#666] tw-mb-4 tw-m-0">
                Write a message or skip to open the conversation.
              </p>
              <textarea
                value={this.state.messageModalText}
                onChange={function(e) { self.setState({ messageModalText: e.target.value }); }}
                placeholder="Type your message (optional)..."
                rows="3"
                className="tw-w-full tw-px-3 tw-py-2.5 tw-text-sm tw-rounded-lg tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-bg-[#f9f9f9] dark:tw-bg-[#141414] tw-text-[#1a1a1a] dark:tw-text-white tw-outline-none focus:tw-border-brand-gold tw-resize-none tw-mb-4"
                autoFocus
              />
              <div className="tw-flex tw-justify-end tw-gap-2">
                <button
                  onClick={function() { self.setState({ messageModalOtherId: null }); }}
                  className="tw-px-4 tw-py-2 tw-rounded-lg tw-text-sm tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-bg-transparent tw-text-[#666] dark:tw-text-[#999] tw-cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={self.sendAndNavigate}
                  disabled={self.state.messagingOrderId}
                  className="tw-px-4 tw-py-2 tw-rounded-lg tw-bg-brand-gold tw-text-white tw-text-sm tw-font-medium tw-border-none tw-cursor-pointer hover:tw-bg-brand-gold/90 tw-transition-colors disabled:tw-opacity-60 disabled:tw-cursor-not-allowed"
                >
                  {self.state.messagingOrderId ? 'Opening...' : (self.state.messageModalText.trim() ? 'Send & Open' : 'Open Chat')}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  }
}

export default OrderHistory;
